import { PageHeader, LoadingState } from '../components/AdminPageUI';
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Grid, Card, CardContent, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, ListItemSecondaryAction, Chip, Divider } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, LocationOn as LocationIcon, AttachMoney as AttachMoneyIcon } from '@mui/icons-material';
import api from '../utils/Api';
import { toast } from 'react-toastify';

export default function TransportRoutesPage() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form State
  const [newRouteName, setNewRouteName] = useState('');
  const [newDefaultFee, setNewDefaultFee] = useState(0);
  const [stops, setStops] = useState([]);
  const [currentStopName, setCurrentStopName] = useState('');

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const res = await api.get('/transport/routes');
      setRoutes(res.data.routes || []);
    } catch (err) {
      toast.error('Failed to load transport routes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  const handleAddStop = () => {
    if (!currentStopName.trim()) return;
    setStops([...stops, currentStopName.trim()]);
    setCurrentStopName('');
  };

  const handleRemoveStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  const handleCreateRoute = async () => {
    if (!newRouteName.trim() || stops.length === 0) {
      toast.warning('Route name and at least one stop are required.');
      return;
    }

    try {
      setIsSubmitting(true);
      await api.post('/transport/routes', {
        name: newRouteName,
        stops,
        defaultFee: Number(newDefaultFee)
      });
      toast.success('Route created successfully!');
      setOpenDialog(false);
      
      // Reset form
      setNewRouteName('');
      setNewDefaultFee(0);
      setStops([]);
      
      fetchRoutes();
    } catch (err) {
      toast.error('Failed to create route');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRoute = async (id) => {
    if (!window.confirm('Are you sure you want to delete this route? This may affect student enrollments.')) return;
    try {
      await api.delete(`/transport/routes/${id}`);
      toast.success('Route deleted');
      fetchRoutes();
    } catch (err) {
      toast.error('Failed to delete route');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title={<>Transport Routes</>} description={<>Manage routes, stops, and transport fees.</>} actions={<>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
                Add New Route
              </Button>
      </>}></PageHeader>

      {loading ? (
        <LoadingState />
      ) : routes.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">No routes defined yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Click "Add New Route" to set up your first transport route.</Typography>
          <Button variant="outlined" onClick={() => setOpenDialog(true)}>Create Route</Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {routes.map((route) => (
            <Grid size={{ xs: 12, md: 6 }}    key={route._id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {route.name}
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => handleDeleteRoute(route._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AttachMoneyIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Default Fee: GHS {route.defaultFee || 0}
                    </Typography>
                  </Box>

                  <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" /> Route Stops (In Order)
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {route.stops.map((stop, i) => (
                      <Chip 
                        key={i} 
                        label={<span style={{ fontWeight: 'bold' }}>{i + 1}. {stop}</span>} 
                        size="small" 
                        color="primary" 
                        variant={i === 0 || i === route.stops.length - 1 ? "filled" : "outlined"} 
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Route Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Transport Route</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}  >
              <TextField
                fullWidth
                label="Route Name (e.g., East Legon - Campus)"
                value={newRouteName}
                onChange={(e) => setNewRouteName(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}  >
              <TextField
                fullWidth
                label="Default Term Fee (GHS)"
                type="number"
                value={newDefaultFee}
                onChange={(e) => setNewDefaultFee(e.target.value)}
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}  >
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Add Stops (In Order of Drop-off)</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Stop Name"
                  value={currentStopName}
                  onChange={(e) => setCurrentStopName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddStop()}
                />
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddStop}>
                  Add
                </Button>
              </Box>
              
              <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, border: stops.length > 0 ? '1px solid #eee' : 'none' }}>
                {stops.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>No stops added yet</Typography>}
                {stops.map((stop, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={`${index + 1}. ${stop}`} />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small" color="error" onClick={() => handleRemoveStop(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateRoute} 
            variant="contained" 
            disabled={isSubmitting || stops.length === 0 || !newRouteName}
          >
            {isSubmitting ? 'Creating...' : 'Create Route'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
